import { sectionKey } from '@/lib/sectionKey';
// ----- Wave E.2 — PlacementTab ----- //
//
// Shows the current CTD section assignment for a document; lets a
// permitted user (Documents:write) change it with an optional reason
// that gets captured in the Part 11 AuditLog `after` payload.
//
// Section dropdown is populated from a passed-in `sections` list (caller
// derives it from the existing section tree built in Wave E.1).
//
// Save flow:
//   1. User selects new section + optionally types a reason.
//   2. Click "Save placement" → calls v2.documents.updatePlacement().
//   3. On success → `onSuccess(updated)` callback so the parent can
//      refresh the document row + bubble a toast.
//   4. On error → render parseApiErrorBody-formatted message inline.

import { useEffect, useState, type ReactElement } from 'react';
import { ApiError, v2 } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { bandForPercent, confidencePercent } from '@/lib/confidence';

export interface SectionOption {
  readonly code: string;
  readonly label: string;
}

export interface PlacementTabProps {
  readonly documentId: string;
  readonly currentSectionCode: string | null;
  readonly sections: readonly SectionOption[];
  readonly canEdit: boolean;
  readonly onSuccess: (newSectionCode: string) => void;
  /**
   * Phase compiler-inline-fix-validation (TODO-023) — when the AI's
   * classifier suggested a different section than the current placement,
   * the caller passes that suggestion here. The tab then renders a
   * "Reclassify to suggested section" button that pre-fills the picker
   * with the suggested code (the user still has to click Save).
   * `null` / equal-to-current → button is hidden.
   */
  readonly suggestedSectionCode?: string | null;
  /**
   * Phase phase-review-surface-strengthen — confidence score (0–100)
   * from the classifier. Shown alongside the suggestion banner so the
   * reviewer knows how much to trust the AI's placement recommendation.
   */
  readonly classifierConfidence?: number | null;
  /**
   * Phase phase-review-surface-strengthen — whether this placement has
   * been confirmed by a human. Drives the visual state of the current
   * section display.
   */
  readonly isConfirmed?: boolean;
}

const ERROR_MESSAGES: Record<string, string> = {
  NotFound: 'This document no longer exists. The list may be out of date.',
  ValidationError: 'Pick a valid section before saving.',
  PermissionDenied: 'You do not have permission to change placement.',
};

export function PlacementTab({
  documentId,
  currentSectionCode,
  sections,
  canEdit,
  onSuccess,
  suggestedSectionCode = null,
  classifierConfidence = null,
  isConfirmed = false,
}: PlacementTabProps): ReactElement {
  const [selected, setSelected] = useState<string>(currentSectionCode ?? '');
  const [reason, setReason] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setSelected(currentSectionCode ?? ''); }, [documentId, currentSectionCode]);
  const selectedOption = sections.find(section => sectionKey(section.code) === sectionKey(selected));
  const suggestedOption = sections.find(section => sectionKey(section.code) === sectionKey(suggestedSectionCode));
  const hasChange = selected !== '' && sectionKey(selected) !== sectionKey(currentSectionCode);

  const handleSave = async (): Promise<void> => {
    if (!hasChange || saving) return;
    setSaving(true);
    setError(null);
    try {
      await v2.documents.updatePlacement(documentId, {
        newSectionCode: selected,
        reason: reason.trim() === '' ? null : reason.trim(),
      });
      onSuccess(selected);
      setReason('');
    } catch (err) {
      setError(formatApiError(err, 'Could not save placement.', ERROR_MESSAGES));
      // ApiError instances carry HTTP status; surface non-4xx separately
      // for clarity (network errors aren't user fault).
      if (!(err instanceof ApiError)) {
        globalThis.console.error('[PlacementTab] unexpected error', err);
      }
    } finally {
      setSaving(false);
    }
  };

  // phase-compiler-screen-batch item 2 — normalisation via the shared helper.
  const confidencePct = confidencePercent({ ClassificationConfidence: classifierConfidence });

  return (
    <div data-testid="placement-tab" style={{ padding: '8px 4px' }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: '#6B7280',
            }}
          >
            Current section
          </div>
          {/* Phase phase-review-surface-strengthen — confirmed vs suggested indicator */}
          {isConfirmed ? (
            <span
              data-testid="placement-confirmed-badge"
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#15803D',
                background: 'rgba(34,197,94,0.15)',
                padding: '1px 7px',
                borderRadius: 10,
                letterSpacing: '0.05em',
              }}
            >
              ✓ CONFIRMED
            </span>
          ) : (
            <span
              data-testid="placement-unconfirmed-badge"
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#92400E',
                background: 'rgba(245,158,11,0.15)',
                padding: '1px 7px',
                borderRadius: 10,
                letterSpacing: '0.05em',
              }}
            >
              ⚠ UNCONFIRMED
            </span>
          )}
        </div>
        <div data-testid="placement-current-section" style={{ fontSize: 13, fontWeight: 600 }}>
          {currentSectionCode ?? '(unassigned)'}
        </div>
      </div>

      {/* Phase compiler-inline-fix-validation (TODO-023) — when the AI
          classifier disagrees with the current placement and that suggestion
          is in the section catalogue, surface a one-click pre-fill. */}
      {suggestedSectionCode &&
      sectionKey(suggestedSectionCode) !== sectionKey(currentSectionCode) &&
      suggestedOption ? (
        <div
          data-testid="placement-suggestion-banner"
          style={{
            padding: '8px 10px',
            background: 'rgba(245,158,11,0.10)',
            border: '1px solid rgba(245,158,11,0.40)',
            borderRadius: 4,
            fontSize: 12,
            marginBottom: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', marginBottom: confidencePct !== null ? 4 : 0 }}>
            <span>
              AI suggests <strong>{suggestedSectionCode}</strong> for this document.
            </span>
            <button
              type="button"
              data-testid="placement-use-suggestion-btn"
              onClick={() => setSelected(suggestedOption.code)}
              disabled={!canEdit || saving}
              style={{
                background: 'transparent',
                border: '1px solid currentColor',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 11,
                fontWeight: 600,
                cursor: canEdit && !saving ? 'pointer' : 'not-allowed',
                color: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              Reclassify to suggested
            </button>
          </div>
          {/* Phase phase-review-surface-strengthen — show confidence alongside suggestion */}
          {confidencePct !== null && (
            <div
              data-testid="placement-suggestion-confidence"
              style={{ fontSize: 11, color: '#92400E', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span>AI confidence:</span>
              <span style={{ fontWeight: 700 }}>
                {confidencePct}%
                {/* phase-compiler-screen-batch item 2 — bands from the shared
                    helper rather than inline 40/70 literals. */}
                {bandForPercent(confidencePct) === 'low'
                  ? ' — LOW, verify carefully'
                  : bandForPercent(confidencePct) === 'medium'
                    ? ' — MEDIUM'
                    : ' — HIGH'}
              </span>
            </div>
          )}
        </div>
      ) : null}

      <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4 }}>
        New section
      </label>
      <select
        data-testid="placement-section-select"
        value={selectedOption?.code ?? selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={!canEdit || saving}
        style={{
          width: '100%',
          padding: '6px 8px',
          fontSize: 13,
          marginBottom: 8,
          borderRadius: 4,
          border: '1px solid #D1D5DB',
        }}
      >
        <option value="">— Select a section —</option>
        {sections.map((s) => (
          <option key={s.code} value={s.code}>
            {s.code} — {s.label}
          </option>
        ))}
      </select>

      {hasChange && (
        <>
          <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4 }}>
            Reason (optional)
          </label>
          <textarea
            data-testid="placement-reason-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={saving}
            rows={2}
            style={{
              width: '100%',
              padding: '6px 8px',
              fontSize: 13,
              marginBottom: 8,
              borderRadius: 4,
              border: '1px solid #D1D5DB',
              fontFamily: 'inherit',
            }}
          />
        </>
      )}

      {error && (
        <div
          data-testid="placement-error"
          role="alert"
          style={{
            padding: '6px 10px',
            background: '#FEE2E2',
            color: '#B91C1C',
            border: '1px solid #B91C1C',
            borderRadius: 4,
            fontSize: 12,
            marginBottom: 8,
          }}
        >
          {error}
        </div>
      )}

      <button
        type="button"
        data-testid="placement-save-btn"
        onClick={() => void handleSave()}
        disabled={!hasChange || saving || !canEdit}
        style={{
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          background: hasChange && !saving && canEdit ? 'var(--accent, #2563EB)' : '#E5E7EB',
          color: hasChange && !saving && canEdit ? '#fff' : '#6B7280',
          border: 'none',
          borderRadius: 4,
          cursor: hasChange && !saving && canEdit ? 'pointer' : 'not-allowed',
        }}
      >
        {saving ? 'Saving…' : 'Save placement'}
      </button>
    </div>
  );
}
